<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const releaseOwners = ref(false);
const syncKanban = ref(true);
const isSaving = ref(false);
const saveError = ref(false);
const isDirty = ref(false);
const applyingFromInbox = ref(false);

const canEdit = computed(() => Boolean(isAdmin.value));

const applyFromInbox = inbox => {
  applyingFromInbox.value = true;
  releaseOwners.value = Boolean(inbox?.release_conversation_owners_on_resolve);
  syncKanban.value = inbox?.sync_conversation_kanban_assignees !== false;
  isDirty.value = false;
  saveError.value = false;
  isSaving.value = false;
  applyingFromInbox.value = false;
};

watch(
  () => props.inbox?.id,
  () => {
    applyFromInbox(props.inbox);
  },
  { immediate: true }
);

watch(
  () => [
    props.inbox?.release_conversation_owners_on_resolve,
    props.inbox?.sync_conversation_kanban_assignees,
  ],
  () => {
    if (isSaving.value || isDirty.value) return;
    applyFromInbox(props.inbox);
  }
);

const markDirty = () => {
  if (applyingFromInbox.value) return;
  isDirty.value = true;
};

const saveTeamWorkSettings = async () => {
  if (!canEdit.value || isSaving.value || !props.inbox?.id) return;

  const savingInboxId = props.inbox.id;
  const payload = {
    id: savingInboxId,
    formData: false,
    release_conversation_owners_on_resolve: releaseOwners.value,
    sync_conversation_kanban_assignees: syncKanban.value,
  };
  isSaving.value = true;
  saveError.value = false;
  try {
    await store.dispatch('inboxes/updateInbox', payload);
    if (props.inbox?.id !== savingInboxId) return;
    isDirty.value = false;
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    if (props.inbox?.id !== savingInboxId) return;
    saveError.value = true;
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    if (props.inbox?.id === savingInboxId) {
      isSaving.value = false;
    }
  }
};
</script>

<template>
  <SettingsFieldSection
    class="mt-6"
    data-testid="inbox-team-work-settings"
    :label="t('INBOX_MGMT.TEAM_WORK.TITLE')"
    :help-text="t('INBOX_MGMT.TEAM_WORK.DESCRIPTION')"
  >
    <div class="flex flex-col gap-2">
      <SettingsToggleSection
        v-model="releaseOwners"
        compact
        :disabled="!canEdit || isSaving"
        :header="t('INBOX_MGMT.TEAM_WORK.RELEASE_OWNERS.TITLE')"
        :description="t('INBOX_MGMT.TEAM_WORK.RELEASE_OWNERS.DESCRIPTION')"
        :toggle-aria-label="t('INBOX_MGMT.TEAM_WORK.RELEASE_OWNERS.TITLE')"
        @update:model-value="markDirty"
      />
      <SettingsToggleSection
        v-model="syncKanban"
        compact
        :disabled="!canEdit || isSaving"
        :header="t('INBOX_MGMT.TEAM_WORK.SYNC_KANBAN.TITLE')"
        :description="t('INBOX_MGMT.TEAM_WORK.SYNC_KANBAN.DESCRIPTION')"
        :toggle-aria-label="t('INBOX_MGMT.TEAM_WORK.SYNC_KANBAN.TITLE')"
        @update:model-value="markDirty"
      />
      <p v-if="saveError" class="text-label-small text-n-ruby-11" role="alert">
        {{ t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE') }}
      </p>
      <p v-if="!canEdit" class="text-label-small text-n-slate-11">
        {{ t('INBOX_MGMT.TEAM_WORK.ADMIN_ONLY') }}
      </p>
    </div>

    <template v-if="canEdit" #extra>
      <div class="grid grid-cols-1 lg:grid-cols-8">
        <div class="col-span-1 lg:col-span-2" />
        <div class="col-span-1 lg:col-span-6 mt-4 justify-self-end">
          <NextButton
            :label="t('INBOX_MGMT.TEAM_WORK.UPDATE')"
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="saveTeamWorkSettings"
          />
        </div>
      </div>
    </template>
  </SettingsFieldSection>
</template>
